import {
    Drawer,
    Box,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    Alert,
    Button,
    Chip,
    Tooltip,
    Divider,
    alpha
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useEffect, useState } from "react";
import { NotificationService } from "../_service/NotificationService";
import { Utils } from "../utils";
import { useAppSelector } from "../_app";

// Notification type
interface Notification {
    id: number;
    event_type: string;
    title: string;
    notification_type: string;
    is_read: boolean;
    message?: string;
    read_at: string | null;
    created_at: string;
}

const PAGE_SIZE = 10;

interface ApiResponse {
    results: Notification[];
    unread_count: number;
    count: number; // Total number of notifications
    next: string | null;
    previous: string | null;
}

const HeaderNotificationPopup = ({
    open,
    onClose,
}: {
    open: boolean;
    onClose: (value: boolean) => void;
}) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [markingAll, setMarkingAll] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0); // Track total notifications
    const employeeId = useAppSelector((state) => state.user.employee_id);

    const fetchNotifications = async (pageNumber = 1, refresh = false) => {
        try {
            pageNumber === 1 ? setLoading(true) : setLoadingMore(true);
            setError(null);

            const res = await NotificationService._getNotification(
                null,
                pageNumber,
                PAGE_SIZE,
                employeeId
            );

            const apiData: ApiResponse = res?.data;
            const newNotifications: Notification[] = apiData?.results || [];

            setNotifications(prev => {
                if (refresh || pageNumber === 1) return newNotifications;
                return [...prev, ...newNotifications];
            });

            setUnreadCount(apiData?.unread_count || 0);
            setTotalCount(apiData?.count || 0);

            // Check if we have more notifications to load
            // Compare total loaded items with total count from API
            const currentLoadedCount = refresh || pageNumber === 1 
                ? newNotifications.length 
                : notifications.length + newNotifications.length;
            
            if (currentLoadedCount >= (apiData?.count || 0)) {
                setHasMore(false);
            } else {
                setHasMore(true);
            }
        } catch {
            setError("Failed to load notifications");
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    // Mark single notification as read
    const markAsRead = async (id: number) => {
        setNotifications((prev) =>
            prev.map((n) =>
                n.id === id
                    ? { ...n, is_read: true, read_at: new Date().toISOString() }
                    : n
            )
        );

        try {
            await NotificationService._toggleNotification("read", { notification_ids: [id] });
            // Update unread count after marking as read
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch {
            setNotifications((prev) =>
                prev.map((n) =>
                    n.id === id ? { ...n, is_read: false, read_at: null } : n
                )
            );
        }
    };

    // Mark all notifications as read
    const markAllAsRead = async () => {
        const unreadIds = notifications
            .filter(n => !n.is_read)
            .map(n => n.id);

        if (unreadIds.length === 0) return;

        setMarkingAll(true);

        // Optimistically update UI
        setNotifications(prev =>
            prev.map(n => n.is_read ? n : { ...n, is_read: true, read_at: new Date().toISOString() })
        );
        
        // Update unread count optimistically
        setUnreadCount(0);

        try {
            await NotificationService._toggleNotification("read", { notification_ids: unreadIds });
        } catch (error) {
            // Revert on error
            setNotifications(prev =>
                prev.map(n => {
                    if (unreadIds.includes(n.id)) {
                        return { ...n, is_read: false, read_at: null };
                    }
                    return n;
                })
            );
            // Revert unread count
            setUnreadCount(unreadIds.length);
            setError("Failed to mark all as read");
        } finally {
            setMarkingAll(false);
        }
    };

    // Load more notifications
    const loadMoreNotifications = () => {
        if (!hasMore || loadingMore) return;
        const nextPage = page + 1;
        setPage(nextPage);
        fetchNotifications(nextPage, false);
    };

    // Reset on Open
    useEffect(() => {
        if (open) {
            setPage(1);
            setHasMore(true);
            setTotalCount(0);
            fetchNotifications(1, true);
        }
    }, [open]);

    const handleRefresh = () => {
        setPage(1);
        setHasMore(true);
        setTotalCount(0);
        fetchNotifications(1, true);
    };

    // Sort notifications: unread first, then by date
    const sortedNotifications = [...notifications].sort((a, b) => {
        if (a.is_read !== b.is_read) {
            return a.is_read ? 1 : -1;
        }
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={() => onClose(false)}
            PaperProps={{
                sx: {
                    width: "35vw",
                }
            }}
        >
            <Box height="100%" display="flex" flexDirection="column" bgcolor="background.default">
                {/* Header */}
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    px={3}
                    py={2}
                    sx={{
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        bgcolor: "#283a44"
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Typography variant="h6" color={"#EEEEEE"} fontWeight={600}>
                            Notifications
                        </Typography>
                        {unreadCount > 0 && (
                            <Chip
                                label={`${unreadCount} unread`}
                                size="small"
                                color="primary"
                                sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                    fontWeight: 500
                                }}
                            />
                        )}
                    </Box>

                    <Box display="flex" paddingLeft={'1px'} gap={1}>
                        <Tooltip title="Refresh">
                            <IconButton
                                size="small"
                                onClick={handleRefresh}
                                sx={{
                                    bgcolor: 'action.hover',
                                    '&:hover': { bgcolor: 'action.selected' }
                                }}
                            >
                                <RefreshIcon fontSize="small" sx={{ color: "#EEEEEE" }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Close">
                            <IconButton
                                size="small"
                                onClick={() => onClose(false)}
                                sx={{
                                    bgcolor: 'action.hover',
                                    '&:hover': { bgcolor: 'action.selected' }
                                }}
                            >
                                <CloseIcon fontSize="small" sx={{ color: "#EEEEEE" }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Action Bar */}
                {notifications.length > 0 && unreadCount > 0 && (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        px={3}
                        py={1.5}
                        sx={{
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            bgcolor: 'background.paper'
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                        </Typography>
                        <Button
                            size="small"
                            variant="text"
                            startIcon={markingAll ? <CircularProgress size={16} /> : <MarkEmailReadIcon />}
                            onClick={markAllAsRead}
                            disabled={markingAll || unreadCount === 0}
                            sx={{
                                fontSize: '0.8125rem',
                                fontWeight: 500,
                                textTransform: 'none'
                            }}
                        >
                            Mark all as read
                        </Button>
                    </Box>
                )}

                {/* Content */}
                <Box
                    flex={1}
                    overflow="auto"
                    paddingLeft={'2px'}
                >
                    {error && (
                        <Alert
                            severity="error"
                            sx={{
                                mx: 2.5,
                                mt: 1,
                                borderRadius: 1.5
                            }}
                            onClose={() => setError(null)}
                        >
                            {error}
                        </Alert>
                    )}

                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                            <CircularProgress size={32} />
                        </Box>
                    ) : notifications.length === 0 ? (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            height="100%"
                            p={3}
                            textAlign="center"
                        >
                            <CheckCircleIcon
                                sx={{
                                    fontSize: 64,
                                    color: 'success.light',
                                    mb: 2,
                                    opacity: 0.8
                                }}
                            />
                            <Typography variant="h6" fontWeight={500} gutterBottom>
                                All caught up!
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                You're all up to date with your notifications
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <List disablePadding>
                                {sortedNotifications.map((item, index) => (
                                    <Box key={item.id}>
                                        <ListItem
                                            sx={{
                                                px: 1,
                                                py: 1,
                                                mx: 0,
                                                mb: 0,
                                                gap: 2,
                                                cursor: 'pointer',
                                                position: 'relative',
                                                transition: 'all 0.2s ease',
                                                bgcolor: item.is_read
                                                    ? 'background.paper'
                                                    : alpha('#2e7d32', 0.06),
                                                '&:hover': {
                                                    bgcolor: item.is_read
                                                        ? 'action.hover'
                                                        : alpha('#2e7d32', 0.1),
                                                },

                                            }}
                                            onClick={() => {
                                                if (!item.is_read) {
                                                    markAsRead(item.id);
                                                }
                                            }}
                                        >

                                            {/* Content */}
                                            <Box flex={1}>
                                                <ListItemText
                                                    primary={
                                                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                                                            <Typography
                                                                variant="caption"
                                                                fontWeight={item.is_read ? 500 : 600}
                                                                color={item.is_read ? 'text.primary' : 'text.primary'}
                                                                sx={{
                                                                    pl: 1,
                                                                    lineHeight: 1.4,
                                                                    fontSize: '0.90rem',
                                                                    borderLeft: !item.is_read
                                                                        ? '4px solid'
                                                                        : '4px solid transparent',
                                                                    borderLeftColor: 'primary.main'

                                                                }}
                                                            >
                                                                {item.title}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ whiteSpace: 'nowrap' }}
                                                            >
                                                                {Utils.formatDateforNotification(item.created_at)}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <>
                                                            {item.message && (
                                                                <Typography
                                                                    variant="body2"
                                                                    color="text.secondary"
                                                                    sx={{
                                                                        mt: 0.5,
                                                                        lineHeight: 1.5,
                                                                        fontSize: '0.80rem'
                                                                    }}
                                                                >
                                                                    {item.message}
                                                                </Typography>
                                                            )}
                                                        </>
                                                    }
                                                    disableTypography
                                                />
                                            </Box>
                                        </ListItem>
                                        {index < sortedNotifications.length - 1 && (
                                            <Divider variant="middle" sx={{ mx: 3 }} />
                                        )}
                                    </Box>
                                ))}
                            </List>

                            {/* Load More Button - Only show if there are more notifications to load */}
                            {hasMore && notifications.length > 0 && (
                                <Box display="flex" justifyContent="center" py={2} px={3}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={loadMoreNotifications}
                                        disabled={loadingMore}
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '0.875rem',
                                            fontWeight: 500,
                                            py: 1,
                                            borderRadius: 1.5
                                        }}
                                    >
                                        {loadingMore ? (
                                            <>
                                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                                Loading...
                                            </>
                                        ) : (
                                            `Load More (${notifications.length} of ${totalCount})`
                                        )}
                                    </Button>
                                </Box>
                            )}

                            {/* Show message when no more notifications */}
                            {!hasMore && notifications.length > 0 && (
                                <Box textAlign="center" py={2}>
                                    <Typography variant="caption" color="text.secondary">
                                        Showing {notifications.length} of {totalCount} notifications
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </Drawer>
    );
};

export default HeaderNotificationPopup;